import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IUser, UserApi } from '../api/UserApi';
import AppBarContext from '../context/AppBarContext';
import VerbaScholaStudentPageContent from '../component/VerbaScholaStudentPageContent';
import { useStudentAccount } from '../component/VerbaScholaStudentShell';
import { STUDENT_INTER as INTER, STUDENT_NAVY as NAVY, STUDENT_POPPINS as POPPINS, STUDENT_SLATE as SLATE, studentBlueButtonSx, studentCardSx } from '../style/verbaScholaStudentStyles';
import { setUserLocalStorage } from './Login';

export default function VerbaScholaStudentAccountPage() {
    const history = useHistory();
    const { setIsLoggedIn } = React.useContext(AppBarContext);
    const account = useStudentAccount();
    const user = account.user;
    const [email, setEmail] = React.useState('');
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [saved, setSaved] = React.useState('');

    React.useEffect(() => { if (user) setEmail(user.email ?? ''); }, [user]);
    const emailChanged = Boolean(user && email.trim() !== (user.email ?? ''));
    const passwordChanged = password.length > 0 || confirmPassword.length > 0;
    const changed = emailChanged || passwordChanged;

    async function save(event: React.FormEvent) {
        event.preventDefault();
        if (!user || !changed || saving) return;
        setError(''); setSaved('');
        if (!currentPassword) { setError('Enter your current password to confirm this change.'); return; }
        if (!email.trim()) { setError('Enter an email address.'); return; }
        if (passwordChanged && (!password || password !== confirmPassword)) {
            setError('New password and confirmation must match.'); return;
        }
        if (passwordChanged && !password.trim()) {
            setError('Enter a password with non-space characters.'); return;
        }
        setSaving(true);
        try {
            const update: IUser = { current_password: currentPassword };
            if (emailChanged) update.email = email.trim();
            if (passwordChanged) update.password = password;
            await UserApi.update(update);
            if (passwordChanged) {
                localStorage.removeItem('jwt');
                setIsLoggedIn(false);
                history.replace('/login', { passwordChanged: true });
                return;
            }
            const response = await UserApi.get();
            account.setUser(response.data);
            setUserLocalStorage(response.data);
            setCurrentPassword(''); setPassword(''); setConfirmPassword('');
            setSaved(emailChanged ? 'Account updated. Check your new email for verification.' : 'Account updated.');
        } catch (caught) {
            const response = caught as { response?: { data?: { message?: string } } };
            setError(response.response?.data?.message ?? 'Could not update your account. Please try again.');
        } finally { setSaving(false); }
    }

    return <VerbaScholaStudentPageContent maxWidth={760}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
            <LockOutlinedIcon sx={{ color: NAVY }} />
            <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, sm: 29 }, color: NAVY }}>Account and password</Typography>
        </Stack>
        {account.loading && !user ? <CircularProgress aria-label="Loading account" /> : !user ?
            <Alert severity="error" action={<Button onClick={account.reload}>Retry</Button>}>{account.error ?? 'Could not load your account.'}</Alert> :
            <Box component="form" onSubmit={event => void save(event)} sx={{ ...studentCardSx, p: { xs: 2.5, sm: 3.5 } }}>
                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, lineHeight: 1.6, mb: 2.5 }}>
                    Change your sign-in email or password. Your current password is required to save either change.
                </Typography>
                <Stack spacing={2}>
                    <TextField label="Email" type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" required fullWidth />
                    <TextField label="Current password" type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoComplete="current-password" fullWidth />
                    <TextField label="New password" type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" helperText="Leave blank to keep your current password." fullWidth />
                    <TextField label="Confirm new password" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" fullWidth />
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 2 }} role="alert">{error}</Alert>}
                {saved && <Alert severity="success" sx={{ mt: 2 }} role="status">{saved}</Alert>}
                <Button type="submit" variant="contained" disabled={!changed || saving} sx={{ ...studentBlueButtonSx, mt: 3, minWidth: 160 }}>
                    {saving ? 'Saving…' : 'Save account'}
                </Button>
            </Box>}
    </VerbaScholaStudentPageContent>;
}
